import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Users, PhoneCall } from "lucide-react";
import { scrollToId } from "@/App";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=2000&q=90";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.12 },
  }),
};

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-gradient-to-b from-emerald-50/60 via-white to-white">
      <div className="absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(16,185,129,0.10),transparent)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-8 text-center">
        <motion.span
          custom={0} variants={fadeUp} initial="hidden" animate="show"
          data-testid="hero-badge"
          className="text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200/60 inline-flex items-center gap-1.5"
        >
          New Zealand &amp; Australia Growth Partner
        </motion.span>

        <motion.h1
          custom={1} variants={fadeUp} initial="hidden" animate="show"
          data-testid="hero-heading"
          className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.12] max-w-4xl mx-auto"
        >
          You run the business.{" "}
          <span className="text-emerald-600">We'll help you grow it.</span>
        </motion.h1>

        <motion.p
          custom={2} variants={fadeUp} initial="hidden" animate="show"
          data-testid="hero-subtext"
          className="mt-6 text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
        >
          We connect you with the right people and create commercial opportunities
          to drive revenue — commercial growth, real estate support, sales training,
          marketing and strategy, all under one roof.
        </motion.p>

        <motion.div
          custom={3} variants={fadeUp} initial="hidden" animate="show"
          className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            data-testid="hero-book-call-btn"
            onClick={() => scrollToId("contact")}
            className="group flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/25 transition-all hover:shadow-xl hover:-translate-y-0.5"
          >
            Book a Strategy Call
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
          <p className="text-sm text-slate-500">
            No lock-in • Dedicated specialists across NZ &amp; AU
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 48 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20"
      >
        <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
          <img
            src={HERO_IMG}
            alt="SORTD growth team collaborating on commercial strategy"
            className="w-full h-[320px] sm:h-[440px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
        </div>

        <div className="hidden md:flex absolute -left-2 lg:left-6 top-10 items-center gap-3 bg-white rounded-xl border border-slate-200 shadow-xl px-4 py-3">
          <span className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </span>
          <div className="text-left">
            <p className="text-sm font-bold text-slate-900">Pipeline Moving</p>
            <p className="text-xs text-slate-500">Prospecting &amp; follow-ups handled</p>
          </div>
        </div>
        <div className="hidden md:flex absolute -right-2 lg:right-6 bottom-16 items-center gap-3 bg-white rounded-xl border border-slate-200 shadow-xl px-4 py-3">
          <span className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Users className="w-4 h-4 text-emerald-600" />
          </span>
          <div className="text-left">
            <p className="text-sm font-bold text-slate-900">Right People</p>
            <p className="text-xs text-slate-500">Doors opened across industries</p>
          </div>
        </div>
        <div className="hidden md:flex absolute right-10 top-6 items-center gap-3 bg-white rounded-xl border border-slate-200 shadow-xl px-4 py-3">
          <span className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
            <PhoneCall className="w-4 h-4 text-emerald-600" />
          </span>
          <div className="text-left">
            <p className="text-sm font-bold text-slate-900">Real Conversations</p>
            <p className="text-xs text-slate-500">Turned into long-term customers</p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
